import disabled from 'ally.js/esm/maintain/disabled';
import Headroom from 'headroom.js';
import debounce from 'lodash/debounce';
import {checkVerticalresize, getHeight, toggleBodyScroll, triggerEvent} from '../base/utils';
import A11y from './a11y';
import SearchBar from './search-bar';

class TopBar {
    constructor($element) {
        if (!$element)
            return;

        this.$mainHeader = $element;
        this.$mainHeaderWrap = $element.querySelector('.header-wrap');
        this.$breadcrumb = document.querySelector('.breadcrumb-wrap');
        this.$breadcrumbTitle = this.$breadcrumb ? this.$breadcrumb.querySelector('li:last-child') : null;
        this.$menuTrigger = document.querySelectorAll('.js-menu-trigger');
        this.$langTrigger = $element.querySelector('.js-lang-trigger');
        this.$langList = $element.querySelector('.js-lang-list');

        this.$menu = document.querySelector('.menu');

        this.$submenuTrigger = $element.querySelectorAll('.submenu-toggle > a');
        this.$submenuLvl2 = $element.querySelectorAll('.menu-level-1-item .submenu, .menu-level-lowest-item .submenu');
        this.$menuBack = $element.querySelectorAll('.menu-back');

        this.windowWidth = document.documentElement.clientWidth;

        this.initTopBar();
        this.initHeadroom();
        this.initMenu();
        this.initLang();
        new SearchBar($element, this);
    }

    initTopBar() {
        window.addEventListener('resize', debounce(this.preventAnimation.bind(this), 0));
        window.addEventListener('resize', debounce(this.conditionalHandleResize.bind(this), 100));
        window.addEventListener('scroll', debounce(this.fixBreadcrumb.bind(this), 50, {trailing: true}));
        window.addEventListener('load', this.handleResize.bind(this));
        this.setHeight();
    }

    initHeadroom() {
        let headroom = new Headroom(document.body, {
            tolerance: {
                up: 20,
                down: 0
            }
        });

        headroom.init();
    }

    fixBreadcrumb() {
        if (!this.$breadcrumb)
            return;

        let top = (window.pageYOffset || document.documentElement.scrollTop) === 0;

        if (top) {
            clearTimeout(this.timeout);
            this.$breadcrumb.classList.add('on-top');
            this.$breadcrumb.classList.remove('not-on-top');
            this.$breadcrumb.classList.remove('nowrap');
        } else {
            clearTimeout(this.timeout);
            this.$breadcrumb.classList.add('not-on-top');
            this.$breadcrumb.classList.remove('on-top');
            this.timeout = setTimeout(() => {
                this.$breadcrumb.classList.add('nowrap');
            }, 400);
        }
    }

    conditionalHandleResize() {
        checkVerticalresize(this.windowWidth, windowWidth => {
            this.windowWidth = windowWidth;
            this.handleResize();
        });
    }

    handleResize() {
        // this.windowWidth = document.documentElement.clientWidth;

        this.setHeight();
        this.resizeMenu();
        this.setSubmenuHeights();
    }

    preventAnimation() {
        checkVerticalresize(this.windowWidth, () => {
            this.$mainHeader.classList.add('no-animation');
            setTimeout(() => { this.$mainHeader.classList.remove('no-animation'); }, 600);
        });
    }

    resizeMenu() {
        let isOpen = document.body.classList.contains('menu-open');

        if (this.windowWidth >= 1024 && isOpen) {
            this.toggleMenu(false);
        }
    }

    setHeight() {
        let headerHeight = Math.min(getHeight(this.$mainHeaderWrap, 'full'), 150),
            breadcrumbHeight = 0,
            titleHeight = 0;

        if (this.$breadcrumb) {
            if (this.windowWidth < 1024) {
                breadcrumbHeight = getHeight(this.$breadcrumb, 'full', 200, () => {
                    this.$breadcrumbTitle.style.maxHeight = 'none';
                    titleHeight = this.$breadcrumbTitle.offsetHeight;
                });

                this.$breadcrumb.style.top = headerHeight + 'px';
                this.$breadcrumbTitle.style.maxHeight = titleHeight + 'px';
            } else {
                this.$breadcrumbTitle.style.maxHeight = '';
                this.$breadcrumb.style.top = '';
            }
        }

        document.body.style.paddingTop = breadcrumbHeight + 'px';
        document.body.style.borderTopWidth = headerHeight + 'px';
    }

    initMenu() {
        this.setSubmenuHeights();

        for (let el of this.$menuTrigger) {
            el.addEventListener('click', () => this.toggleMenu());
        }
        for (let el of this.$submenuTrigger) {
            el.addEventListener('click', this.toggleSubmenu.bind(this));
        }
        for (let el of this.$menuBack) {
            el.addEventListener('click', this.closeSubmenu.bind(this));
        }
    }

    setSubmenuHeights() {
        for (let el of this.$submenuLvl2) {
            el.style.maxHeight = el.querySelector('.submenu-wrap').offsetHeight + 'px';
        }
    }

    toggleMenu(doOpen) {
        if (typeof doOpen === 'undefined') {
            doOpen = !document.body.classList.contains('menu-open');
        }

        if (doOpen) {
            this.menuA11yHandlers = A11y.focusTrap(this.$mainHeader.querySelector('.header-main-content'), this.$menuTrigger[0], this.toggleMenu.bind(this, false));
        } else {
            if (this.menuA11yHandlers) this.menuA11yHandlers = A11y.removeFocusTrap(this.menuA11yHandlers);

            setTimeout(() => {
                for (let el of this.$menu.querySelectorAll('.active')) {
                    el.classList.remove('active');
                }
                for (let el of this.$menu.querySelectorAll('.active-trail')) {
                    el.classList.add('active');
                }
            }, 200);
        }

        document.body.classList.toggle('menu-open');
        triggerEvent(document.body, 'menu', doOpen);
        toggleBodyScroll(doOpen);
    }

    toggleSubmenu(e) {
        if (document.body.classList.contains('menu-open')) {
            e.preventDefault();

            e.target.parentNode.classList.toggle('active');
        }
    }

    closeSubmenu(e) {
        let $menuItem = e.target.parentNode.parentNode;

        e.preventDefault();

        $menuItem.classList.remove('active');
        $menuItem.querySelector('a').focus();
    }

    initLang() {
        if (!this.$langTrigger) {
            return;
        }

        this.outsideClickHandler = this.outsideClick.bind(this);

        this.$langTrigger.addEventListener('click', () => {
            this.$langList.classList.toggle('lang-open');
            this.$langTrigger.setAttribute('aria-expanded', 'true');
            this.$langList.querySelector('.is-active').focus();

            this.langA11yHandlers = A11y.focusTrap(this.$langList, this.$langTrigger, this.hideLang.bind(this));

            document.body.addEventListener('click', this.outsideClickHandler);
        });
    }

    hideLang() {
        this.$langList.classList.remove('lang-open');
        this.$langTrigger.removeAttribute('aria-expanded');

        this.langA11yHandlers = A11y.removeFocusTrap(this.langA11yHandlers);
        document.body.removeEventListener('click', this.outsideClickHandler);
    }

    outsideClick(e) {
        if (!this.$langList.contains(e.target) && !this.$langTrigger.contains(e.target)) {
            e.preventDefault();
            this.hideLang();
        }
    }

    disable($exclude) {
        let $logo = this.$mainHeader.querySelector('.logo');

        this.disableHandler = disabled({
            context: this.$mainHeader,
            filter: [$exclude, $logo]
        });
    }

    enable() {
        if (this.disableHandler) this.disableHandler.disengage();
        this.disableHandler = null;
    }
}

export default TopBar;
